import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BaseFormComponent } from 'components/form/base-form';
import { Router } from '@angular/router';
import { ApiService } from 'services/api.service';
import { AuthorizationService } from 'services/authorization.service';
import { ToastService } from 'services/toast.service';
import {
  BackofficeReadingRoomSubmissionCard,
  ReadingRoomSubmissionRequestedDocumentItem,
  ReadingRoomSubmissionResearchTopicItem,
  UpdateDraftReadingRoomSubmissionRequest,
  UpdateDraftReadingRoomSubmissionRequestedDocumentsInformationRequest
} from 'types/service.interface';
import { Location, NgClass } from '@angular/common';
import { TooltipComponent } from 'components/tooltip/tooltip.component';
import { DocumentInfoModalComponent } from 'components/modal/document-info-modal/document-info-modal.component';
import { ServiceFormActionsComponent } from 'components/service/service-form-actions/service-form-actions.component';
import { MAX_LENGTH_DOCUMENTS_CODE, getControlsWithErrors, MAX_LENGTH_DOCUMENT_NOTE, MAX_LENGTH_DOCUMENTS_NOTE } from 'util/form.utility';
import { ConfirmModalComponent } from 'components/modal/confirm-modal/confirm-modal.component';

@Component({
  selector: 'app-visit-room-document-form',
  standalone: true,
  imports: [
    TranslateModule,
    TooltipComponent,
    ReactiveFormsModule,
    DocumentInfoModalComponent,
    NgClass,
    ServiceFormActionsComponent,
    ConfirmModalComponent
  ],
  templateUrl: './visit-room-document-form.component.html',
  styleUrls: ['./visit-room-document-form.component.scss', '../../../form/base-form.scss']
})
export class VisitRoomDocumentFormComponent extends BaseFormComponent implements OnInit {
  @Input() application!: any;
  @Output()
  applicationUpdated: EventEmitter<BackofficeReadingRoomSubmissionCard> = new EventEmitter<BackofficeReadingRoomSubmissionCard>();
  @Output() saveDraft: EventEmitter<UpdateDraftReadingRoomSubmissionRequest> = new EventEmitter<UpdateDraftReadingRoomSubmissionRequest>();
  @Output() stepChanged: EventEmitter<string> = new EventEmitter<string>();
  @Output() formChanged: EventEmitter<boolean> = new EventEmitter<boolean>();

  readonly maxNotesLength = MAX_LENGTH_DOCUMENT_NOTE;
  readonly maxDocumentCodeLength = MAX_LENGTH_DOCUMENTS_CODE;
  readonly maxDocumentNotesLength = MAX_LENGTH_DOCUMENTS_NOTE;
  isOpenConfirmDeleteSubmission = false;

  get documents(): FormArray {
    return this.form.get('requestedDocuments') as FormArray;
  }
  get requestedDocumentsInformation(): AbstractControl<any, any> | null {
    return this.form.get('requestedDocumentsInformation');
  }
  get hasRequestedDocuments(): AbstractControl<any, any> | null {
    return this.form.get('hasRequestedDocuments');
  }

  constructor(
    protected override location: Location,
    protected override router: Router,
    protected override formBuilder: FormBuilder,
    protected override apiService: ApiService,
    protected authService: AuthorizationService,
    protected override toastService: ToastService,
    protected override translateService: TranslateService
  ) {
    super(location, router, formBuilder, apiService, toastService, translateService);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.form.markAsPristine();
    this.formChanged.emit(false);
    this.form.valueChanges.subscribe(() => {
      this.formChanged.emit(this.form.dirty);
    });
    if (!this.documents.length && !!this.form.get('hasRequestedDocuments')?.value) {
      this.addDocument();
    }
    this.form.get('hasRequestedDocuments')?.valueChanges.subscribe((hasDocs: boolean) => {
      if (!hasDocs) {
        this.documents.clear();
        this.form.get('requestedDocumentsInformation')?.reset('');
        this.form.markAsDirty();
      } else {
        this.addDocument();
      }
    });
  }

  override fillForm(): void {
    this.form = this.formBuilder.group({
      hasRequestedDocuments: [this.application.hasRequestedDocuments || false, Validators.required],
      requestedDocumentsInformation: [
        this.application.requestedDocumentsInformation || '',
        [Validators.maxLength(this.maxDocumentNotesLength)]
      ],
      requestedDocuments: this.formBuilder.array([])
    });

    if (this.application.requestedDocuments) {
      this.application.requestedDocuments.forEach((document: ReadingRoomSubmissionRequestedDocumentItem) => {
        this.documents.push(
          this.formBuilder.group({
            code: document.code,
            notes: document.notes
          })
        );
      });
    }
  }

  addDocument(): void {
    this.documents.push(
      this.formBuilder.group({
        code: ['', [Validators.maxLength(this.maxDocumentCodeLength)]],
        notes: ['', [Validators.maxLength(this.maxNotesLength)]]
      })
    );

    this.form.markAsDirty();
  }

  deleteDocument(index: number): void {
    this.documents.removeAt(index);
    this.form.markAsDirty();
  }

  onSaveDraft(): void {
    this.form.markAsDirty();
  }

  onContinue(): void {
    this.form.markAsDirty();
  }

  override submit(navigate: boolean = true): void {
    this.sanitizeRequestedDocuments();

    if (this.form.valid) {
      if (this.application.initializationStatus === 'PreparedResearchTopics') {
        const args: UpdateDraftReadingRoomSubmissionRequestedDocumentsInformationRequest = {
          readingRoomSubmissionId: this.application.id,
          hasRequestedDocuments: this.form.value.hasRequestedDocuments || false,
          requestedDocuments: this.form.value.requestedDocuments || [],
          requestedDocumentsInformation: this.form.value.requestedDocumentsInformation || ''
        };

        this.apiService.updateVisitRoomDocumentOrder(args).subscribe(
          (data: BackofficeReadingRoomSubmissionCard) => {
            if (navigate) {
              this.applicationUpdated.emit(data);
              this.router.navigate([], {
                queryParams: { solis: 4 },
                queryParamsHandling: 'merge'
              });
            }
          },
          (error: any) => {
            console.error(error);
            this.toastService.showRequestError();
          }
        );
      } else {
        this.handleSaveDraft(true);
      }
    } else {
      this.toastService.showFormValidationError(getControlsWithErrors(this.form), 'pages.service.visitRoom');
    }
  }

  handleSaveDraft(nextStep: boolean = false): void {
    this.sanitizeRequestedDocuments();

    if (this.application) {
      const args: UpdateDraftReadingRoomSubmissionRequest = {
        readingRoomSubmissionId: this.application.id,
        ...this.form.value,
        clientId: this.application.client.id,
        readerCardId: this.application.readerCard.id,
        readingRoomId: this.application.readingRoom.id,
        visitDate: this.application.visitDate,
        visitTimeFrom: this.application.visitTimeFrom,
        visitTimeTo: this.application.visitTimeTo,
        visitInformation: this.application.visitInformation,
        researchTopicIds: this.getResearchTopicsId(),
        agreementToReadingRoomTerms: this.application.agreementToReadingRoomTerms,
        hasRequestedDocuments: this.form.value.hasRequestedDocuments || false,
        requestedDocuments: this.form.value.requestedDocuments || [],
        requestedDocumentsInformation: this.form.value.requestedDocumentsInformation || ''
      };

      this.saveDraft.emit(args);

      if (nextStep) {
        this.router.navigate([], {
          queryParams: { solis: 4 },
          queryParamsHandling: 'merge'
        });
      }
    }
  }

  getResearchTopicsId(): number[] {
    if (this.application) {
      return this.application.researchTopics?.map((topic: ReadingRoomSubmissionResearchTopicItem) => topic.id) || [];
    }

    return [];
  }

  override back(confirmed: boolean = false): void {
    if (!confirmed && this.form.dirty) {
      this.isOpenConfirmBack = true;
    } else {
      this.router.navigate([], {
        queryParams: { solis: 2 },
        queryParamsHandling: 'merge'
      });
    }
  }

  onModalClose(): void {
    this.isOpenConfirmDeleteSubmission = false;
  }

  handleDeleteDraft() {
    this.isOpenConfirmDeleteSubmission = true;
  }

  deleteReadingRoomSubmission() {
    if (this.application?.id) {
      this.apiService.deleteReadingRoomSubmission(this.application?.id).subscribe(
        () => {
          this.router.navigate(['/lasitavas-apmeklejums']);
          this.toastService.showSuccess('applicationDraftDeleted', '', {
            shouldTranslate: true
          });
        },
        (error: any) => {
          console.error(error);
          this.toastService.showRequestError();
        }
      );
    }
  }

  private sanitizeRequestedDocuments(): void {
    const sanitizedDocuments = this.documents.controls.filter((control) => {
      const code = control.get('code')?.value?.trim();
      const notes = control.get('notes')?.value?.trim();
      return code || notes;
    });

    const newFormArray = this.formBuilder.array(
      sanitizedDocuments.map((control) =>
        this.formBuilder.group({
          code: [control.get('code')?.value || '', [Validators.maxLength(this.maxDocumentCodeLength)]],
          notes: [control.get('notes')?.value || '', [Validators.maxLength(this.maxNotesLength)]]
        })
      )
    );

    this.form.setControl('requestedDocuments', newFormArray);
  }
}
